import OrderSummary from '@/components/v1/OrderSummary'
import ButtonSpinner from '@app-studio-web/ui/components/ButtonSpinner'
import TermsCheckbox from '@app-studio-web/ui/components/TermsCheckbox'
import {
  resolveConfidenceVisitorId,
  VISITOR_ID_QUERY_PARAM,
} from '@app-studio-web/ui/helpers/confidenceVisitorId'
import { trackSubscriptionEvent } from '@app-studio-web/ui/helpers/facebookTracking'
import { getStripePriceAmountAndCurrency } from '@app-studio-web/ui/helpers/localizeStripePrice'
import { type GetPricesData } from '@app-studio-web/ui/loaders/getPricesLoader'
import { type Plan } from '@app-studio-web/ui/types'
import { LockClosedIcon, ShieldCheckIcon } from '@heroicons/react/24/solid'
import { PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js'
import { type PaymentIntent } from '@stripe/stripe-js'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'

interface Props {
  clientSecret: string
  currency: string
  intentMode: 'payment' | 'setup'
  plan: Plan
  pricesData: GetPricesData
  subscriptionId: string
  trialPeriodDays?: number
  userEmail: string | undefined
}

/**
 * li-funnel-redesign v1 — the payment form.
 *
 * A fork of the shared CheckoutForm rather than a wrapper around it. The
 * shared one is on the baseline token namespace (`text-custom-error`, the
 * `custom-button-primary` button) with no way to override either, so the v1
 * page was a restyled shell around a baseline form.
 *
 * The Stripe handling below is copied verbatim and deliberately so — in
 * particular the visitor-id passing, whose correctness argument is load
 * bearing. Keep the two in step.
 */
export default function CheckoutForm({
  clientSecret,
  currency,
  intentMode,
  plan,
  pricesData,
  subscriptionId,
  trialPeriodDays,
  userEmail,
}: Props) {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isTermsAccepted, setIsTermsAccepted] = useState(false)
  const [message, setMessage] = useState<string>()
  const [isIntentReady, setIsIntentReady] = useState(false)
  const [paymentIntent, setPaymentIntent] = useState<PaymentIntent>()

  const elements = useElements()
  const location = useLocation()
  const stripe = useStripe()
  const { t } = useTranslation()

  const stripePriceFull =
    plan === 'one-time'
      ? pricesData.oneTimePrice
      : plan === 'top'
        ? pricesData.topPrice
        : plan === 'first'
          ? pricesData.firstPrice
          : pricesData.secondPrice

  const stripePriceIntro =
    plan === 'one-time'
      ? undefined
      : plan === 'top'
        ? pricesData.topIntroPrice
        : plan === 'first'
          ? pricesData.firstIntroPrice
          : pricesData.secondIntroPrice

  const stripePrice = stripePriceIntro ?? stripePriceFull

  const fullPriceData =
    stripePriceFull && currency
      ? getStripePriceAmountAndCurrency({
          stripePrice: stripePriceFull,
          userCurrency: currency,
        })
      : undefined

  const fullPrice = fullPriceData?.stripePriceAmountInCents
  const fullPriceCurrency = fullPriceData?.stripePriceCurrency

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    if (!elements || !stripe) return

    setIsSubmitting(true)

    const origin = window.location.origin
    const pathname = '/checkout/payment-completion'
    const urlSearchParams = new URLSearchParams(location.search)

    urlSearchParams.append('stripe_first_price_id', stripePriceIntro?.id ?? '')
    urlSearchParams.append('stripe_next_price_id', stripePriceFull?.id ?? '')
    if (subscriptionId) {
      urlSearchParams.append('subscription_id', subscriptionId)
    }

    // Confirming payment hands the browser to Stripe, and coming back is a
    // fresh page load that has to find the visitor id again. Measured on Lingo
    // web, 23% of purchases find nothing there and mint a new id, so the
    // purchase lands on a visitor with no funnel behind it. Passing it through
    // the return URL is the only carrier the browser cannot drop.
    //
    // Cleared before writing, and written with `set` rather than `append`,
    // because these params start as a copy of the current URL's. A visitor
    // arriving on an attacker-crafted `?cnfd_visitor_id=` would otherwise carry
    // it into the return URL, and since `URLSearchParams.get` returns the first
    // of a duplicated key, theirs would win over ours on the way back. The
    // delete also covers the case where we have no id to send: an inbound one
    // must not survive on its own.
    urlSearchParams.delete(VISITOR_ID_QUERY_PARAM)

    const confidenceVisitorId = resolveConfidenceVisitorId()
    if (confidenceVisitorId) {
      urlSearchParams.set(VISITOR_ID_QUERY_PARAM, confidenceVisitorId)
    }

    const returnUrl = `${origin}${pathname}?${urlSearchParams.toString()}`

    const { error } =
      intentMode === 'setup'
        ? await stripe.confirmSetup({
            elements,
            confirmParams: { return_url: returnUrl },
          })
        : await stripe.confirmPayment({
            elements,
            confirmParams: { return_url: returnUrl },
          })

    setIsSubmitting(false)

    if (!error) return

    if (error.type === 'card_error' || error.type === 'validation_error') {
      setMessage(error.message)
    } else {
      throw error
    }
  }

  useEffect(() => {
    if (!clientSecret || !stripe) return

    const retrieveIntent = async () => {
      if (intentMode === 'setup') {
        const { setupIntent } = await stripe.retrieveSetupIntent(clientSecret)
        setIsIntentReady(Boolean(setupIntent))

        if (setupIntent?.last_setup_error) {
          setMessage(t('components.checkoutForm.somethingWentWrong'))
        }

        return
      }

      const { paymentIntent: retrievedPaymentIntent } =
        await stripe.retrievePaymentIntent(clientSecret)
      setPaymentIntent(retrievedPaymentIntent)
      setIsIntentReady(Boolean(retrievedPaymentIntent))

      if (retrievedPaymentIntent?.last_payment_error) {
        if (
          retrievedPaymentIntent.last_payment_error.code ===
          'payment_intent_authentication_failure'
        ) {
          setMessage(t('components.checkoutForm.paymentFailed'))
        } else {
          setMessage(t('components.checkoutForm.somethingWentWrong'))
        }
      }
    }

    retrieveIntent().catch((error) => {
      throw error
    })
  }, [clientSecret, intentMode, stripe, t])

  useEffect(() => {
    if (!isIntentReady) return

    const trackEvent = async () => {
      await trackSubscriptionEvent({
        currency,
        eventType: 'initiate-checkout',
        stripeFirstPriceId: stripePriceIntro?.id ?? '',
        stripeNextPriceId: stripePriceFull?.id ?? '',
      })
    }

    trackEvent().catch((error) => console.error(error))
  }, [currency, isIntentReady, stripePriceFull?.id, stripePriceIntro?.id])

  useEffect(() => {
    const resetSubmittingState = () => {
      setIsSubmitting(false)
    }

    window.addEventListener('pageshow', resetSubmittingState)
    document.addEventListener('visibilitychange', resetSubmittingState)

    return () => {
      window.removeEventListener('pageshow', resetSubmittingState)
      document.removeEventListener('visibilitychange', resetSubmittingState)
    }
  }, [])

  const isDisabled = !elements || !stripe || !isTermsAccepted

  return (
    <form className="flex flex-col gap-5" onSubmit={handleSubmit}>
      <div className="hw-card animate-fade-up p-4">
        <PaymentElement
          options={{
            defaultValues: { billingDetails: { email: userEmail } },
            layout: { defaultCollapsed: false, type: 'tabs' },
            paymentMethodOrder: [
              'link',
              'apple_pay',
              'paypal',
              'card',
              'google_pay',
            ],
          }}
        />
      </div>

      <OrderSummary
        className="[animation-delay:120ms]"
        fullPrice={fullPrice}
        fullPriceCurrency={fullPriceCurrency}
        paymentIntent={paymentIntent}
        plan={plan}
        stripePrice={stripePrice}
        stripePriceFull={stripePriceFull}
        stripePriceIntro={stripePriceIntro}
        trialPeriodDays={trialPeriodDays}
      />

      <div className="animate-fade-up space-y-4 [animation-delay:240ms]">
        <TermsCheckbox
          fullPrice={fullPrice}
          fullPriceCurrency={fullPriceCurrency}
          isTermsAccepted={isTermsAccepted}
          paymentIntent={paymentIntent}
          plan={plan}
          setIsTermsAccepted={setIsTermsAccepted}
          stripePriceFull={stripePriceFull}
          trialPeriodDays={trialPeriodDays}
        />

        {message && (
          <p className="bg-hw-red-soft text-hw-red animate-fade-in rounded-xl p-3 text-center text-sm font-semibold">
            {message}
          </p>
        )}

        <button
          className={`hw-button-primary ${isTermsAccepted ? 'animate-pulse-cta' : ''}`}
          data-testid="button-pay"
          disabled={isDisabled || isSubmitting}
          type="submit"
        >
          {isSubmitting && <ButtonSpinner />}

          {trialPeriodDays
            ? t('pages.checkout.trialPlans.startTrial')
            : plan === 'one-time'
              ? t('buttons.payNow')
              : t('buttons.subscribeNow')}
        </button>

        {!isTermsAccepted && (
          <p className="text-hw-muted text-center text-xs">
            {t('pages.checkout.payment.termsCheckboxHint')}
          </p>
        )}

        {/*
          The reassurance moved here from a panel above the form. Above, it was
          a wall of text between the buyer and the card field; as two badges
          under the button it reads at a glance at the moment of commitment.
        */}
        <div className="flex flex-wrap justify-center gap-2 text-xs font-semibold">
          <span className="bg-hw-green-soft text-hw-green flex items-center gap-1.5 rounded-lg px-3 py-1.5">
            <LockClosedIcon className="h-3.5 w-3.5" />
            {t('pages.checkout.payment.securePaymentTitle')}
          </span>

          <span className="bg-hw-primary-soft text-hw-primary flex items-center gap-1.5 rounded-lg px-3 py-1.5">
            <ShieldCheckIcon className="h-3.5 w-3.5" />
            {t('pages.checkout.payment.moneyBackGuaranteeTitle')}
          </span>
        </div>

        <p className="text-hw-muted text-center text-xs leading-relaxed">
          {t('pages.checkout.payment.securePaymentText')}{' '}
          {t('pages.checkout.payment.moneyBackGuaranteeText')}
        </p>
      </div>
    </form>
  )
}
